import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import ItemLibrary from '../ItemLibrary';

import './ItemEditor.scss';
import Dialog from 'ui/dialog';
import { ItemIcon, WithEngravingTooltip, WithItemTooltip, WithSkillTooltip, WithTripodTooltip } from 'tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import NumberInput from 'ui/number';
import Select from 'ui/select';
import { getIconPath } from 'common';
import { underlinerFunc } from 'ui/select';
import { formatHtml } from 'utils/format';

function getStatId(type, id) {
  if (type === 2) {
    return Data.statsMap[id];
  } else {
    return id.toString();
  }
}

function mergeRanges(ranges) {
  const result = [];
  for (const [a, b] of ranges.sort((a, b) => a[0] - b[0])) {
    if (result.length && a <= result[result.length - 1] + 1) {
      result[result.length - 1] = Math.max(result[result.length - 1], b);
    } else {
      result.push(a, b);
    }
  }
  return result;
}

function* itemEnumerateRandom(data, charClass, stats) {
  const statsFit = Object.keys(stats);
  function* iterate(group, idx) {
    if (idx >= statsFit.length) {
      yield group;
    } else {
      const id = statsFit[idx];
      const value = stats[id];
      for (let si = 0; si < group.length; ++si) {
        const sub = group[si];
        const optIdx = sub.options.findIndex(opt => id === opt.stat && value >= opt.min && value <= opt.max);
        if (optIdx >= 0) {
          const ngroup = group.slice();
          ngroup[si] = {...sub};
          ngroup[si].count -= 1;
          ngroup[si].options.splice(optIdx, 1);
          if (!ngroup[si].count) ngroup.splice(si, 1);
          yield* iterate(ngroup, idx + 1);
        }
      }
    }
  }

  for (const group of data) {
    yield* iterate(group.subgroups.map(sub => {
      const options = [];
      for (let i = 0; i < sub.options.length; i += 6) {
        const [type, id, max, dev, , filterClass] = sub.options.slice(i, i + 6);
        if (charClass && filterClass && Data.classMap[filterClass] !== charClass) continue;
        options.push({stat: getStatId(type, id), min: max - dev, max});
      }
      return {count: sub.count, options, group};
    }), 0);
  }
}

function itemAddRandom(data, charClass, stats, addId, addValue) {
  if (stats[addId]) return stats;

  let bestResult = null;
  for (const group of itemEnumerateRandom(data, charClass, stats)) {
    for (const sub of group) {
      const opt = sub.options.find(opt => opt.stat === addId);
      if (opt) {
        const value = Math.max(opt.min, Math.min(opt.max, addValue));
        if (bestResult == null || Math.abs(bestResult - addValue) > Math.abs(value - addValue)) {
          bestResult = value;
        }
      }
    }
  }

  if (bestResult == null) {
    return stats;
  } else {
    return {...stats, [addId]: bestResult};
  }
}

function itemFillRandom(data, charClass, stats) {
  for (const group of itemEnumerateRandom(data, charClass, stats)) {
    const result = {...stats};
    for (const sub of group) {
      for (const {stat, max} of sub.options.slice(0, sub.count)) {
        result[stat] = max;
      }
    }
    return result;
  }
  return stats;
}

function itemStatOptions(data, charClass, stats) {
  const ranges = {};
  const groups = {};
  for (const group of itemEnumerateRandom(data, charClass, stats)) {
    for (const sub of group) {
      for (const opt of sub.options) {
        ranges[opt.stat] = (ranges[opt.stat] || []);
        ranges[opt.stat].push([opt.min, opt.max]);
        groups[opt.stat] = sub.group;
      }
    }
  }
  const result = {};
  for (const [stat, rng] of Object.entries(ranges)) {
    result[stat] = mergeRanges(rng);
  }
  return [result, groups];
}

function itemSetId(item, id, charClass) {
  const data = Data.items[id];
  if (!data) return null;
  const newItem = {id};
  if (data.quality) {
    if (item && item.upgrade) {
      newItem.upgrade = item.upgrade;
      while (newItem.upgrade > 0 && !Data.itemQuality[`${data.quality}#${100 + newItem.upgrade}`]) {
        newItem.upgrade -= 1;
      }
    } else {
      newItem.upgrade = 0;
    }
  }
  const base = Data.itemStatic[`${data.static0}#${data.grade}`];
  if (base && base.tripod) {
    newItem.tripod = item?.tripod || {};
  }
  if (data.gemSlots) {
    newItem.gems = item?.gems?.slice(0, data.gemSlots) || [];
    while (newItem.gems.length < data.gemSlots) {
      newItem.gems.push(null);
    }
  }
  if (data.engrave) {
    newItem.engraving_extra = item?.engraving_extra || {};
  }
  const random0 = Data.itemRandom[`${data.random0}#${data.grade}`];
  if (random0) {
    newItem.quality = {};
    if (item && item.quality) {
      for (const [id, value] of Object.entries(item.quality)) {
        newItem.quality = itemAddRandom(random0, charClass, newItem.quality, id, value);
      }
    }
    newItem.quality = itemFillRandom(random0, charClass, newItem.quality);
  }
  const random1 = Data.itemRandom[`${data.random1}#${data.grade}`];
  if (random1) {
    newItem.engraving = {};
    if (item && item.engraving) {
      for (const [id, value] of Object.entries(item.engraving)) {
        newItem.engraving = itemAddRandom(random1, charClass, newItem.engraving, id, value);
      }
    }
  }
  if (data.card) {
    newItem.awake = item?.awake || 0;
  }
  return newItem;
}

function itemFixedStats(data, stats) {
  if (data.length !== 1) return false;
  const group = data[0];
  if (group.some(sub => sub.options.length !== sub.count * 6)) return false;
  if (Object.keys(stats).length !== group.reduce((sum, sub) => sum + sub.count, 0)) return false;
  return group.every(sub => {
    for (let i = 0; i < sub.options.length; i += 6) {
      const [stat, id] = sub.options.slice(i, i + 6);
      if (stats[getStatId(stat, id)] == null) {
        return false;
      }
    }
    return true;
  });
}

function ItemSelector({ charClass, slot, onSelect, filterSource }) {
  const itemCategory = React.useMemo(() => ({
    tip: 'tip.name.enum_itemdictionarycategory_all',
    types: new Set(Object.keys(Data.itemCategory).filter(id => Data.itemCategory[id].slot === slot).map(val => parseInt(val))),
    filter: item => !item.broken && (!filterSource || item.sources),
  }), [slot, filterSource]);
  const onClick = React.useCallback((e, id) => onSelect(id), [onSelect]);

  return (
    <Dialog className="lap-ItemSelector" modal title="Select Item" onClose={() => onSelect(null)}>
      <div className="lap-ItemSelector-content">
        <ItemLibrary charClass={charClass} root={itemCategory} hideTree onClick={onClick} hasClose/>
      </div>
    </Dialog>
  );
}

export function ItemDisplay({ item }) {
  const data = Data.items[item.id];
  if (!data) return null;
  return (
    <WithItemTooltip item={item}>
      <div className="lap-item-header-item">
        <div className={`lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color}`}>
          <ItemIcon item={data} upgrade={item.upgrade}/>
        </div>
        <div className={`lap-item-name la-color-${Data.itemGrades[data.grade || 0].color}`}>
          {data.name}
        </div>
      </div>
    </WithItemTooltip>
  );
}

function ItemUpgrade({ item, onChange }) {
  const data = Data.items[item.id];
  const maxUpgrade = React.useMemo(() => {
    if (!data.quality) return 0;
    let upgrade = 0;
    while (Data.itemQuality[`${data.quality}#${100 + upgrade + 1}`]) {
      upgrade += 1;
    }
    return upgrade;
  }, [data]);
  const onUpgrade = React.useCallback(value => onChange(item => ({...item, upgrade: value})), [onChange]);
  if (!maxUpgrade) return null;
  return (
    <ul className="lap-item-stats">
      <li>
        <div className="lap-item-stat-name"><span className="lap-label">Upgrade</span></div>
        <div className="lap-item-stat-value"><NumberInput min={0} max={maxUpgrade} value={item.upgrade || 0} onChange={onUpgrade}/></div>
      </li>
    </ul>
  );
}

function ItemStatLine({ stats, onChange, stat, random, charClass, selectOpt, otherStats }) {
  const [opts, statGroups] = React.useMemo(() => {
    const stats2 = {...stats};
    if (stat) delete stats2[stat];
    let [opts, statGroups] = itemStatOptions(random, charClass, stats2);
    if (selectOpt.bracelet) {
      const otherStats2 = Object.assign({}, ...otherStats);
      if (stat) delete otherStats2[stat];
      const keys = Object.keys(otherStats2);
      const groupCount = new Map();
      for (const id of keys) {
        if (id === stat) continue;
        const g = statGroups[id];
        if (g) groupCount.set(g, (groupCount.get(g) || 0) + 1);
      }
      const newOpts = {};
      for (const [id, ranges] of Object.entries(opts)) {
        if (otherStats2[id]) continue;
        const g = statGroups[id];
        if (g && (groupCount.get(g) || 0) >= g.count) continue;
        newOpts[id] = ranges;
      }
      opts = newOpts;
    }
    return [opts, statGroups];
  }, [stats, random, charClass, stat, selectOpt, otherStats]);
  
  const getOptions = React.useCallback(() => selectOpt.makeOptions(opts, statGroups), [opts, statGroups, selectOpt]);

  const onChangeStat = React.useCallback(opt => onChange(stats => {
    const stats2 = {...stats};
    if (stat) delete stats2[stat];
    if (!opt?.id) return stats2;
    const sinfo = opts[opt.id];
    return itemAddRandom(random, charClass, stats2, opt.id, stats[opt.id] || (sinfo && sinfo[sinfo.length - 1]) || 0);
  }), [stat, onChange, charClass, random, opts]);

  const values = React.useMemo(() => {
    if (!stat || !opts[stat]) return null;
    const decimal = selectOpt.statDecimal(stat);
    if (!decimal) return opts[stat];
    const divisor = Math.pow(10, decimal);
    return opts[stat].map(value => value / divisor);
  }, [opts, stat, selectOpt]);

  const onChangeValue = React.useCallback(value => {
    if (!stat) return;
    const divisor = Math.pow(10, selectOpt.statDecimal(stat));
    onChange(stats => ({...stats, [stat]: Math.round(value * divisor)}));
  }, [stat, onChange, selectOpt]);

  const optLength = Object.keys(opts).length;
  if (!stat && !optLength) return null;

  const digits = (stat && selectOpt.statDecimal(stat)) || 0;
  const divisor = Math.pow(10, digits);
  return (
    <li>
      <div className="lap-item-stat-name">
        <Select className={selectOpt.className}
                options={getOptions}
                value={stat && selectOpt.makeValue(stat)}
                onChange={onChangeStat}
                allowEmpty
                disabled={stat && optLength <= 1}
                searchLimit={10}
                searchMiddle
                renderValue={selectOpt.renderValue}
                renderOption={selectOpt.renderOption}
                placeholder={selectOpt.placeholder}
        />
      </div>
      <div className="lap-item-stat-value">
        {!!stat && !selectOpt.hideValue?.(stat) && (
          <NumberInput values={values}
                       value={stats[stat] / divisor}
                       onChange={onChangeValue}
                       digits={digits}
                       step={1 / divisor}
                       trim={false}
          />
        )}
      </div>
    </li>
  );
}

function ItemRandom({ random, stats, selectOpt, onChange, charClass, otherStats }) {
  return (
    <ul className="lap-item-stats">
      {Object.keys(stats).sort(selectOpt.statCompare).map(stat => (
        <ItemStatLine key={stat}
                      stats={stats}
                      onChange={onChange}
                      stat={stat}
                      random={random}
                      charClass={charClass}
                      otherStats={otherStats}
                      selectOpt={selectOpt}
        />
      ))}
      <ItemStatLine stats={stats}
                    onChange={onChange}
                    stat={null}
                    random={random}
                    charClass={charClass}
                    otherStats={otherStats}
                    selectOpt={selectOpt}
      />
    </ul>
  );
}

const _emptyObject = {};
const _emptyArray = [];

const itemQualitySelect = {
  statDecimal: stat => Data.itemStats[stat]?.decimal || 0,
  statCompare: (a, b) => (Data.itemStats[a]?.id || 0) - (Data.itemStats[b]?.id || 0),
  makeValue: stat => ({id: stat, name: _F(Data.itemStats[stat]?.tip)}),
  makeOptions: stats => Object.keys(stats).sort(itemQualitySelect.statCompare).map(stat => ({id: stat, name: _F(Data.itemStats[stat]?.tip)})),
  placeholder: "Select stat...",
  className: "ui-Select-noframe",
};

function ItemQuality({ item, onChange, charClass }) {
  const data = Data.items[item.id];
  const random = Data.itemRandom[`${data.random0}#${data.grade}`];
  const onChangeStats = React.useCallback(cbk => onChange(item => ({...item, quality: cbk(item.quality || {})})), [onChange]);

  if (!random || !random.length) return null;
  return <ItemRandom random={random} stats={item.quality || _emptyObject} selectOpt={itemQualitySelect} onChange={onChangeStats} charClass={charClass}/>;
}

const itemEngravingSelect = {
  statDecimal: () => 0,
  statCompare: (a, b) => parseInt(a) - parseInt(b),
  makeValue: id => ({id, name: Data.engraving[id]?.name}),
  makeOptions: stats => {
    const catCls = [], catNormal = [], catMalus = [];
    for (const id in stats) {
      const info = Data.engraving[id];
      if (!info) continue;
      const row = {id, name: info.name};
      if (info.class) catCls.push(row);
      else if (info.penalty) catMalus.push(row);
      else catNormal.push(row);
    }
    return [
      {title: 'Class Engravings', options: catCls.sort((a, b) => a.name.localeCompare(b.name))},
      {title: 'Normal Engravings', options: catNormal.sort((a, b) => a.name.localeCompare(b.name))},
      {title: 'Malus Engravings', options: catMalus.sort((a, b) => a.name.localeCompare(b.name))},
    ].filter(cat => cat.options.length);
  },
  placeholder: "Select engraving...",
  renderValue: opt => <WithEngravingTooltip id={opt.id}><span className={classNames({"lap-engraving-malus": Data.engraving[opt.id].penalty})}>{opt.name}</span></WithEngravingTooltip>,
  renderOption: (opt, search) => {
    const info = Data.engraving[opt.id];
    return (
      <WithEngravingTooltip id={opt.id}>
        <span className={classNames({"lap-engraving-malus": Data.engraving[opt.id].penalty})}>
          <span className="lap-engraving-icon" style={{backgroundImage: `url(${getIconPath(info.icon)}`}}/>
          {search ? underlinerFunc(opt.name, search) : opt.name}
        </span>
      </WithEngravingTooltip>
    );
  },
  className: "ui-Select-noframe",
};

function ItemEngraving({ item, onChange, charClass }) {
  const data = Data.items[item.id];
  const random = React.useMemo(() => {
    if (data.abilityStone) {
      const points = data.abilityStone.maxOrbCount;
      return [{subgroups: [
        {count: 2, options: Data.engravingStone[1000].map(id => [3, id, points, points, 0, 0]).flat()},
        {count: 1, options: Data.engravingStone[1010].map(id => [3, id, points, points, 0, 0]).flat()},
      ]}];
    }
    return Data.itemRandom[`${data.random1}#${data.grade}`];
  }, [data]);
  const onChangeStats = React.useCallback(cbk => onChange(item => ({...item, engraving: cbk(item.engraving || {})})), [onChange]);

  if (!random || !random.length) return null;
  return <ItemRandom random={random} stats={item.engraving || _emptyObject} selectOpt={itemEngravingSelect} onChange={onChangeStats} charClass={charClass}/>;
}

const itemEngravingExtraSelect = {
  ...itemEngravingSelect,
  placeholder: "Select extra engraving...",
};

function ItemEngravingExtra({ item, onChange, charClass }) {
  const data = Data.items[item.id];

  const random = React.useMemo(() => [[{count: 1, options: Data.engravingRing}]], []);
  const onChangeStats = React.useCallback(cbk => onChange(item => ({...item, engraving_extra: cbk(item.engraving_extra || {})})), [onChange]);

  if (!data.engrave) return null;

  if (!random || !random.length) return null;
  return <ItemRandom random={random} stats={item.engraving_extra || _emptyObject} selectOpt={itemEngravingExtraSelect} onChange={onChangeStats} charClass={charClass}/>;
}

const engravingPickerSelect = {
  ...itemEngravingSelect,
  className: null,
};

export function EngravingPicker({ value, onChange, charClass }) {
  const onChangeStats = React.useCallback(cbk => onChange(value => Object.entries(cbk(value ? {[value.id]: value.points} : {})).map(([id, points]) => ({id, points}))[0]), [onChange]);
  return (
    <div className="lap-ItemEditor">
      <ItemRandom random={[{subgroups: [{count: 1, options: Data.engravingRing}]}]}
                  stats={value ? {[value.id]: value.points} : {}}
                  selectOpt={engravingPickerSelect}
                  onChange={onChangeStats}
                  charClass={charClass}/>
    </div>
  );
}

const renderSkillValue = opt => (
  <WithSkillTooltip skill={opt.id}>
    <span>{opt.name}</span>
  </WithSkillTooltip>
);
const renderSkillOption = (opt, search) => {
  const info = Data.skills[opt.id];
  return (
    <WithSkillTooltip skill={opt.id}>
      <span className={opt.disabled ? 'lap-disabled' : null}>
        <span className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(info.icon)}`}}/>
        {search ? underlinerFunc(opt.name, search) : opt.name}
      </span>
    </WithSkillTooltip>
  );
};

function ItemTripodLine({ item, onChange, charClass, index }) {
  const key = Object.keys(item.tripod || {})[index];
  const [skill, podStr] = (key || '').split('#');
  const podIndex = podStr && parseInt(podStr);
  const skillOptions = React.useCallback(() => Object.entries(Data.skills)
    .filter(([, skill]) => skill.class === charClass && skill.sort != null && skill.tripods && skill.tripods.some(pod => pod.levels > 1))
    .sort(([, a], [, b]) => a.sort !== b.sort ? a.sort - b.sort : a.learn - b.learn)
    .map(([id, skill]) => {
      const tripods = Object.keys(item.tripod || {});
      tripods.splice(index, 1);
      return {
        id,
        name: skill.name,
        disabled: !skill.tripods.some((pod, idx) => pod.levels > 1 && !tripods.includes(`${id}#${idx + 1}`)),
      };
    }), [charClass, item, index]);
  const podOptions = React.useCallback(() => {
    const tripods = Object.keys(item.tripod || {});
    tripods.splice(index, 1);
    return (Data.skills[skill]?.tripods || []).map((info, index) => info.levels > 1 && {
      id: index + 1,
      name: info.name,
      disabled: tripods.includes(`${skill}#${index + 1}`),
    }).filter(Boolean);
  }, [skill, item, index]);

  const value = item.tripod?.[key] || 0;

  const renderTripodValue = React.useCallback(opt => (
    <WithTripodTooltip skill={skill} tripod={opt.id} level={1} level2={1 + value}>
      <span>{opt.name}</span>
    </WithTripodTooltip>
  ), [skill, value]);
  const renderTripodOption = React.useCallback((opt, search) => {
    const info = Data.skills[skill]?.tripods[opt.id - 1];
    if (!info) return null;
    return (
      <WithTripodTooltip skill={skill} tripod={opt.id} level={1} level2={value ? 1 + value : 5}>
        <span className={opt.disabled ? 'lap-disabled' : null}>
          <span className="lap-tripod-icon">
            <span style={{backgroundImage: `url(${getIconPath(info.icon)}`}}/>
          </span>
          {search ? underlinerFunc(opt.name, search) : opt.name}
        </span>
      </WithTripodTooltip>
    );
  }, [skill, value]);

  const onChangeSkill = React.useCallback(opt => onChange(item => {
    const tripods = Object.entries(item.tripod || {});
    if (tripods[index]?.[0]?.split('#')[0] === opt?.id) return item;
    if (opt) {
      const skill = Data.skills[opt.id];
      if (!skill || !skill.tripods) return item;
      const pod = skill.tripods.findIndex((pod, idx) => pod.levels > 1 && !tripods.some((op, oi) => oi !== index && op[0] === `${opt.id}#${idx + 1}`));
      if (pod < 0) return item;
      if (tripods[index]) {
        tripods[index] = [`${opt.id}#${pod + 1}`, tripods[index][1]];
      } else {
        tripods.push([`${opt.id}#${pod + 1}`, 4]);
      }
    } else if (tripods[index]) {
      tripods.splice(index, 1);
    }
    return {...item, tripod: Object.fromEntries(tripods)};
  }), [onChange, index]);
  const onChangeTripod = React.useCallback(opt => onChange(item => {
    const tripods = Object.entries(item.tripod || {});
    if (tripods[index]?.[0]?.split('#')[1] === opt.id.toString()) return item;
    tripods[index] = [`${skill}#${opt.id}`, tripods[index]?.[1] || 4];
    return {...item, tripod: Object.fromEntries(tripods)};
  }), [onChange, skill, index]);
  const onChangeValue = React.useCallback(value => onChange(item => {
    const tripods = Object.entries(item.tripod || {});
    if (!tripods[index] || tripods[index][1] === value) return item;
    tripods[index] = [tripods[index][0], value];
    return {...item, tripod: Object.fromEntries(tripods)};
  }), [onChange, index]);
  
  return (
    <li>
      <div className="lap-item-stat-name">
        <div>
          <Select className="ui-Select-noframe"
                  options={skillOptions}
                  value={skill ? {id: skill, name: Data.skills[skill]?.name} : null}
                  onChange={onChangeSkill}
                  searchLimit={10}
                  renderValue={renderSkillValue}
                  renderOption={renderSkillOption}
                  allowEmpty
                  placeholder="Select skill..."
                  />
        </div>
        {!!key && (
          <div className="lap-item-tripod-line">
            <Select className="ui-Select-noframe"
                    options={podOptions}
                    value={podIndex && ({id: podIndex, name: Data.skills[skill]?.tripods[podIndex - 1]?.name})}
                    onChange={onChangeTripod}
                    disabled={!skill}
                    renderValue={renderTripodValue}
                    renderOption={renderTripodOption}
                    placeholder="Select tripod..."
                    />
          </div>
        )}
      </div>
      <div className="lap-item-stat-value">
        {!!key && (
          <NumberInput min={1} max={4}
                       value={value}
                       onChange={onChangeValue}/>
        )}
      </div>
    </li>
  );
}

function ItemTripod({ item, onChange, charClass }) {
  const data = Data.items[item.id];
  const base = Data.itemStatic[`${data.static0}#${data.grade}`];
  if (!base || !base.tripod) return null;

  return (
    <ul className="lap-item-stats">
      <ItemTripodLine item={item} onChange={onChange} charClass={charClass} index={0}/>
      <ItemTripodLine item={item} onChange={onChange} charClass={charClass} index={1}/>
      <ItemTripodLine item={item} onChange={onChange} charClass={charClass} index={2}/>
    </ul>
  );
}

function statName(id) {
  if (Data.itemStats[id]) {
    return Data.strings[Data.itemStats[id].tip];
  } else if (Data.combatEffect[id]) {
    return Data.combatEffect[id];
  } else {
    return `Unknown stat`;
  }
}

function renderBraceletStat({id}) {
  if (Data.itemStats[id]) {
    return <span>{_F(Data.itemStats[id].tip)}</span>;
  } else if (Data.combatEffect[id]) {
    return <span>{formatHtml(Data.combatEffect[id])}</span>;
  } else {
    return <span>Unknown stat</span>;
  }
}

function braceletOptions(stats, statGroups) {
  const groups = new Map();
  for (const id of Object.keys(stats)) {
    const group = statGroups[id] || null;
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(id);
  }
  const result = [];
  for (const [group, opts] of groups) {
    let dst = result;
    if (group && group.name) {
      dst = [];
      result.push({title: group.name, options: dst});
    }
    opts.sort(itemBraceletSelect.statCompare);
    for (const id of opts) {
      dst.push({id, name: statName(id)});
    }
  }
  return result;
}

const itemBraceletSelect = {
  hideValue: stat => !Data.itemStats[stat],
  statDecimal: stat => Data.itemStats[stat]?.decimal || 0,
  statCompare: (a, b) => (Data.itemStats[a]?.id || a) - (Data.itemStats[b]?.id || b),
  makeValue: stat => ({id: stat, name: statName(stat)}),
  renderValue: renderBraceletStat,
  renderOption: renderBraceletStat,
  makeOptions: braceletOptions,
  placeholder: "Select stat...",
  className: "ui-Select-noframe",
  bracelet: true,
};

function ItemBraceletLine({ item, onChange, index, charClass }) {
  const onChangeStats = React.useCallback(value => onChange(item => {
    const prev = item.bracelet?.[index] || {};
    if (typeof value === 'function') value = value(prev);
    if (value === prev) return item;
    item = {...item};
    item.bracelet = item.bracelet?.slice() || [];
    item.bracelet[index] = value;
    return item;
  }), [onChange, index]);

  const data = Data.items[item.id];
  const random = data.bracelet && Data.itemRandom[`${data.bracelet.option}#${data.grade}`];
  if (!random || !random.length) return null;

  const braceletStats = item.bracelet || [];

  return <>
    {!!braceletStats[index] && Object.keys(braceletStats[index]).sort(itemBraceletSelect.statCompare).map(stat => (
      <ItemStatLine key={stat}
                    stats={braceletStats[index]}
                    onChange={onChangeStats}
                    stat={stat}
                    random={random}
                    charClass={charClass}
                    otherStats={braceletStats}
                    selectOpt={itemBraceletSelect}
      />
    ))}
    <ItemStatLine stats={braceletStats[index] || _emptyObject}
                  onChange={onChangeStats}
                  stat={null}
                  random={random}
                  charClass={charClass}
                  otherStats={braceletStats}
                  selectOpt={itemBraceletSelect}
    />
  </>;
}

function ItemBracelet({ item, onChange, charClass }) {
  const data = Data.items[item.id];
  const random = data.bracelet && Data.itemRandom[`${data.bracelet.option}#${data.grade}`];

  const maxOptions = React.useMemo(() => {
    let maxOptions = 0;
    if (!data.bracelet) return 0;
    if (data.bracelet.fixed2) maxOptions += 2;
    else if (data.bracelet.fixed1) maxOptions += 1;
    if (data.bracelet.enchant3) maxOptions += 3;
    else if (data.bracelet.enchant2) maxOptions += 2;
    else if (data.bracelet.enchant1) maxOptions += 1;
    return maxOptions;
  }, [data.bracelet]);

  if (!random || !random.length) return null;

  return (
    <ul className="lap-item-stats">
      {[...Array(maxOptions)].map((_, index) => <ItemBraceletLine key={index} item={item} onChange={onChange} charClass={charClass} index={index}/>)}
    </ul>
  );
}

export default function ItemEditor({ charClass, slot, item, onChange, filterSource }) {
  const [selecting, setSelecting] = React.useState(false);

  if (!item || !Data.items[item.id]) item = null;

  const onSelectItem = React.useCallback(id => {
    setSelecting(false);
    if (id) onChange(item => itemSetId(item, id, charClass))
  }, [charClass, onChange]);
  const onClear = React.useCallback(e => {
    e.stopPropagation();
    onChange(null);
  }, [onChange]);

  return (
    <div className="lap-ItemEditor">
      {!!selecting && <ItemSelector charClass={charClass} slot={slot} onSelect={onSelectItem} filterSource={filterSource}/>}
      <div className="lap-item-header" onClick={() => setSelecting(true)}>
        {item ? <ItemDisplay item={item}/> : <div className="lap-item-header-empty">Empty Slot</div>}
        {!!item && <FontAwesomeIcon icon={faTimes} className="lap-item-remove" onClick={onClear}/>}
      </div>
      {!!item && <>
        <ItemUpgrade item={item} onChange={onChange}/>
        <ItemQuality item={item} onChange={onChange} charClass={charClass}/>
        <ItemEngraving item={item} onChange={onChange} charClass={charClass}/>
        {/* <ItemEngravingExtra item={item} onChange={onChange} charClass={charClass}/> */}
        <ItemTripod item={item} onChange={onChange} charClass={charClass}/>
        <ItemBracelet item={item} onChange={onChange} charClass={charClass}/>
      </>}
    </div>
  );
}
